/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';

export const getProtocolValueAmountColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
): SmartTableColDef => ({
  colId: `amount`,
  sortable: true,
  headerValueGetter: () => {
    // FIXME
    // if (protocolValue && protocolValue.is_output) {
    //   return 'Initial Amount';
    // }
    return 'Amount Used';
  },
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: (params: HeaderComponentParamsProps) => {
    return {
      toolPanelColumnName: `Amount`,
      icon: ICONS.protocol_value.info.amount_used,
      experimentOrProtocol,
    };
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolValueService.getEditable(params),
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return null;
    }

    if (params.data && params.data.amount) {
      return `${params.data.amount} ${params.data.unit || ''}`;
    }

    return '';
  },
  cellRenderer: (params: CellRendererParams<ProtocolValue>) => {
    const fallback = (
      <DataGridPlaceholder
        params={params}
        editable={GridSmartTableProtocolValueService.getEditable(
          params,
        )}
        placeholder="No amount"
      />
    );

    if (params.data && params.data.amount) {
      return params.data.amountUnitText || fallback;
    }

    return fallback;
  },
  valueSetter: (params: any): boolean => {
    if (!params.node.data) {
      return false;
    }

    const cellState =
      GridSmartTableProtocolValueService.getCellState(params);
    if (cellState === 'loading') {
      return false;
    }

    const newValue = params.newValue;
    const regex = /(\d+(?:[.,]\d+)?)(\s*[a-zA-Zµ°±²³´¹ΩΔ\/]*)/;
    const match = newValue.replace(',', '.').match(regex);
    if (!match) {
      return false;
    }
    const amount = match[1];
    const unit = match[2].trim();

    const protocolValue = params.data as ProtocolValue;

    if (protocolValue) {
      if (protocolValue.guid[0] === '-') {
        return false;
      }

      return GridSmartTableProtocolValueService.updateEntity(
        params,
        protocolValue.entityName,
        protocolValue.idAttr,
        {
          amount,
          unit,
        },
      );
    }

    const body: { [key: string]: any } = {
      [`${experimentOrProtocol.entityName}_id`]:
        experimentOrProtocol.guid,
      ...(params.node.data?.id && {
        protocol_condition_id: params.node.data.id,
      }),
      amount: amount,
      unit: unit,
    };

    return GridSmartTableProtocolValueService.createEntity(
      params,
      ProtocolValue.entityName,
      body,
      'protocol_condition',
      params.node.data.guid,
    );
  },
  // FIXME
  // cellStyle: (params) =>
  //   GridSmartTableProtocolValueService.getCellStyle(
  //     params,
  //     experimentOrProtocol,
  //     ProtocolValue.entityName,
  //     name,
  //   ),
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
  valueClearer: () => {
    return '0';
  },
});
